<template>
    <section
        class="complement-bandeau"
        aria-label="Compléments après clôture"
    >
        <i class="fas fa-lock-open" aria-hidden="true"></i>
        <p>
            Clôturée le <strong>{{ date(consultation.complement.cloture_le) }}</strong> · compléments possibles jusqu’au
            <strong>{{ date(consultation.complement.jusqu_au) }}</strong>
        </p>
        <button
            v-if="corrigeable"
            type="button"
            class="complement-corriger"
            @click="ouvrir"
        >
            Corriger l’issue
        </button>
        <small v-else>Issue non corrigeable ici (référence ou hospitalisation)</small>
        <b-modal
            v-model="visible"
            title="Corriger l’issue de la consultation"
            :no-close-on-backdrop="chargement"
            :no-close-on-esc="chargement"
            :hide-header-close="chargement"
            @hide="avant_fermeture"
        >
            <form
                id="correction-issue"
                @submit.prevent="enregistrer"
            >
                <label for="correction-issue-valeur">Issue</label>
                <select
                    id="correction-issue-valeur"
                    v-model="issue"
                    class="form-control"
                    :disabled="chargement"
                    required
                >
                    <option
                        v-for="option in issues"
                        :key="option.value"
                        :value="option.value"
                        :disabled="interdite(option.value)"
                    >
                        {{ option.label }}
                    </option>
                </select>
                <p class="complement-annotation">
                    Référence et hospitalisation : non corrigeables en raison de leurs effets d’orientation.
                </p>
                <label for="correction-evolution">Évolution clinique</label>
                <select
                    id="correction-evolution"
                    v-model="evolution"
                    class="form-control"
                    :disabled="chargement"
                    required
                >
                    <option
                        v-for="option in evolutions"
                        :key="option.value"
                        :value="option.value"
                    >
                        {{ option.label }}
                    </option>
                </select>
                <label for="correction-motif">Motif obligatoire (5 à 500 caractères)</label>
                <textarea
                    id="correction-motif"
                    v-model="motif"
                    class="form-control"
                    rows="3"
                    minlength="5"
                    maxlength="500"
                    :disabled="chargement"
                    required
                ></textarea>
                <p
                    v-if="erreur"
                    role="alert"
                >
                    {{ erreur }}
                </p>
                <p
                    v-if="conflit"
                    role="status"
                >
                    Les valeurs ont changé. Le dossier a été relu : {{ libelle_issue(attendu.issue_consultation) }} ·
                    {{ libelle_evolution(attendu.evolution_clinique) }}. Vérifiez votre saisie puis recommencez.
                </p>
            </form>
            <template #modal-footer>
                <button
                    type="button"
                    class="btn btn-outline-secondary rounded-pill"
                    :disabled="chargement"
                    @click="visible = false"
                >
                    Annuler
                </button>
                <button
                    type="submit"
                    form="correction-issue"
                    class="btn btn-primary rounded-pill"
                    :disabled="!valide || chargement"
                >
                    {{ chargement ? 'Enregistrement…' : conflit ? 'Recommencer' : 'Enregistrer' }}
                </button>
            </template>
        </b-modal>
    </section>
</template>
<script>
import api from '@/api/_consultation'
import { dateComplement, fuseauComplement } from '@/utils/complement-date'
import { consultation_issues, clinical_evolutions, issueLabel, evolutionLabel } from '@/utils/consultation-issues'

export default {
    name: 'ComplementBandeau',
    props: { consultation: { type: Object, required: true } },
    data() {
        return {
            visible: false,
            chargement: false,
            erreur: '',
            conflit: false,
            relue: true,
            issue: '',
            evolution: '',
            motif: '',
            attendu: {},
            issues: consultation_issues.concat([{ value: 'hospitalisation', label: 'Hospitalisation indiquée' }]),
            evolutions: clinical_evolutions,
        }
    },
    computed: {
        corrigeable() {
            return !this.interdite(this.consultation.issue_consultation)
        },
        valide() {
            const longueur = this.motif.trim().length
            return (
                this.relue &&
                this.corrigeable &&
                !this.interdite(this.issue) &&
                this.issues.some((option) => option.value === this.issue) &&
                this.evolutions.some((option) => option.value === this.evolution) &&
                longueur >= 5 &&
                longueur <= 500
            )
        },
    },
    methods: {
        date(instant) {
            return dateComplement(instant, this.consultation.timezone)
        },
        interdite(issue) {
            return ['reference', 'hospitalisation'].includes(issue)
        },
        libelle_issue: issueLabel,
        libelle_evolution: evolutionLabel,
        avant_fermeture(evenement) {
            if (this.chargement) evenement.preventDefault()
        },
        valeurs(consultation) {
            return { issue_consultation: consultation.issue_consultation, evolution_clinique: consultation.evolution_clinique }
        },
        ouvrir() {
            if (!this.corrigeable) return
            this.attendu = this.valeurs(this.consultation)
            this.issue = this.attendu.issue_consultation
            this.evolution = this.attendu.evolution_clinique
            this.motif = ''
            this.erreur = ''
            this.conflit = false
            this.relue = true
            this.visible = true
        },
        async relire() {
            const consultation = this.consultation
            const lecture = await api.ouvrirConsultationTableauMedecin({
                consultation_id: consultation.id,
                patient_id: consultation.patient_id,
                tarif_consultation_id: consultation.tarif_consultation_id || (consultation.tarif_consultation || {}).id,
            })
            if (!lecture || Number(lecture.id) !== Number(consultation.id)) throw new Error('Le dossier n’a pas pu être relu.')
            if (this._isDestroyed || Number(this.consultation.id) !== Number(consultation.id)) return
            this.attendu = this.valeurs(lecture)
            this.$emit('corrigee', { ...lecture, timezone: fuseauComplement(lecture.complement && lecture.complement.fuseau) })
            this.conflit = true
            this.relue = true
        },
        async enregistrer() {
            if (!this.valide || this.chargement) return
            const consultation = this.consultation
            this.chargement = true
            this.erreur = ''
            try {
                const acces = await api.getClotureMedecinAccess()
                if (this._isDestroyed || Number(this.consultation.id) !== Number(consultation.id)) return
                if (!acces || !acces.success || !acces.data || !acces.data.access_token) {
                    throw new Error(acces && acces.message || 'Autorisation de correction indisponible.')
                }
                const resultat = await api.corrigerIssueConsultation(consultation.id, {
                    issue_consultation: this.issue,
                    evolution_clinique: this.evolution,
                    motif: this.motif.trim(),
                    attendu: { ...this.attendu },
                }, acces.data.access_token)
                if (this._isDestroyed || Number(this.consultation.id) !== Number(consultation.id)) return
                if (!resultat || !resultat.success || Number(resultat.consultation_id) !== Number(consultation.id)) {
                    throw new Error('La correction n’a pas été confirmée.')
                }
                this.$emit('corrigee', { ...consultation, ...this.valeurs(resultat) })
                this.visible = false
            } catch (error) {
                if (this._isDestroyed || Number(this.consultation.id) !== Number(consultation.id)) return
                const data = (error && error.response && error.response.data) || {}
                this.erreur = data.message || ''
                if (data.code === 'valeurs_modifiees') {
                    this.relue = false
                    try {
                        await this.relire()
                    } catch (lecture) {
                        const reponse = lecture && lecture.response && lecture.response.data
                        this.erreur = reponse && reponse.message ||
                            'La relecture a échoué. Fermez cette fenêtre et rouvrez le dossier avant de corriger.'
                    }
                } else this.erreur = data.message || error.message || 'La correction a échoué.'
            } finally {
                this.chargement = false
            }
        },
    },
}
</script>
<style scoped lang="scss">
// Une seule ligne fine au-dessus de la carte : l'état du complément et l'action de correction.
.complement-bandeau {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 12px;
    min-height: 40px;
    padding: 5px 6px 5px 16px;
    margin-bottom: 14px;
    border: 1px solid #dce6f8;
    border-radius: 999px;
    background: #f5f8fe;
    color: var(--ui-text, #172033);
    font-size: 13px;
    line-height: 1.35;
    > i { color: var(--ui-accent, #2457c5); }
    > p { flex: 1 1 auto; margin: 0; }
    > small { color: var(--ui-text-secondary, #273449); }
}
.complement-corriger {
    min-height: 30px;
    padding: 0 14px;
    border: 1px solid #c9d7f2;
    border-radius: 999px;
    background: #fff;
    color: var(--ui-accent, #2457c5);
    font-size: 12.5px;
    font-weight: 600;
}
.complement-annotation {
    color: var(--ui-text-secondary, #273449);
    margin: 8px 0 16px;
}
label {
    display: block;
    margin-top: 14px;
    color: var(--ui-text-secondary, #273449);
}
button:focus-visible {
    outline: 2px solid var(--ui-accent, #2457c5);
    outline-offset: 3px;
}
@media (max-width: 800px) {
    .complement-bandeau { border-radius: 16px; padding: 8px 12px; }
}
</style>
